import api from "@/utils/axios";
import type { PagedResponse } from "@/types/pagination.types";
import type {
  CreateTicketMessagePayload,
  CreateTicketPayload,
  TicketDetail,
  TicketStatus,
  TicketStorageSummary,
  TicketSummary,
  UpdateTicketStatusPayload,
} from "@/types/ticket.types";

const buildFormData = (payload: unknown, attachments?: File[]) => {
  const formData = new FormData();
  formData.append(
    "payload",
    new Blob([JSON.stringify(payload)], { type: "application/json" })
  );
  if (attachments?.length) {
    attachments.forEach((file) => formData.append("attachments", file));
  }
  return formData;
};

const buildListParams = (page?: number, size?: number, status?: TicketStatus) => {
  const params = new URLSearchParams();
  if (page !== undefined) params.set("page", String(page));
  if (size !== undefined) params.set("size", String(size));
  if (status) params.set("status", status);
  return params.toString();
};

const openBlobInNewTab = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const ticketService = {
  async getMyTickets(params?: {
    page?: number;
    size?: number;
    status?: TicketStatus;
  }): Promise<PagedResponse<TicketSummary>> {
    const query = buildListParams(params?.page, params?.size, params?.status);
    const response = await api.get<PagedResponse<TicketSummary>>(
      `/account/tickets${query ? `?${query}` : ""}`
    );
    return response.data;
  },

  async getMyTicket(ticketId: string): Promise<TicketDetail> {
    const response = await api.get<TicketDetail>(
      `/account/tickets/${encodeURIComponent(ticketId)}`
    );
    return response.data;
  },

  async createMyTicket(payload: CreateTicketPayload, attachments?: File[]): Promise<TicketDetail> {
    const response = await api.post<TicketDetail>(
      "/account/tickets",
      buildFormData(payload, attachments),
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  async createMyTicketMessage(
    ticketId: string,
    payload: CreateTicketMessagePayload,
    attachments?: File[]
  ): Promise<TicketDetail> {
    const response = await api.post<TicketDetail>(
      `/account/tickets/${encodeURIComponent(ticketId)}/messages`,
      buildFormData(payload, attachments),
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  async updateMyTicketStatus(
    ticketId: string,
    payload: UpdateTicketStatusPayload
  ): Promise<TicketDetail> {
    const response = await api.patch<TicketDetail>(
      `/account/tickets/${encodeURIComponent(ticketId)}/status`,
      payload
    );
    return response.data;
  },

  async getMyAttachmentBlob(ticketId: string, attachmentId: string): Promise<Blob> {
    const response = await api.get(
      `/account/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { responseType: "blob" }
    );
    return response.data as Blob;
  },

  async openMyAttachment(ticketId: string, attachmentId: string): Promise<void> {
    const blob = await ticketService.getMyAttachmentBlob(ticketId, attachmentId);
    openBlobInNewTab(blob);
  },

  async getAdminTickets(params?: {
    page?: number;
    size?: number;
    status?: TicketStatus;
  }): Promise<PagedResponse<TicketSummary>> {
    const query = buildListParams(params?.page, params?.size, params?.status);
    const response = await api.get<PagedResponse<TicketSummary>>(
      `/admin/tickets${query ? `?${query}` : ""}`
    );
    return response.data;
  },

  async getAdminTicket(ticketId: string): Promise<TicketDetail> {
    const response = await api.get<TicketDetail>(
      `/admin/tickets/${encodeURIComponent(ticketId)}`
    );
    return response.data;
  },

  async createAdminTicketMessage(
    ticketId: string,
    payload: CreateTicketMessagePayload,
    attachments?: File[]
  ): Promise<TicketDetail> {
    const response = await api.post<TicketDetail>(
      `/admin/tickets/${encodeURIComponent(ticketId)}/messages`,
      buildFormData(payload, attachments),
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  async updateAdminTicketStatus(
    ticketId: string,
    payload: UpdateTicketStatusPayload
  ): Promise<TicketDetail> {
    const response = await api.patch<TicketDetail>(
      `/admin/tickets/${encodeURIComponent(ticketId)}/status`,
      payload
    );
    return response.data;
  },

  async deleteClosedTicket(ticketId: string): Promise<void> {
    await api.delete(`/admin/tickets/${encodeURIComponent(ticketId)}`);
  },

  async deleteTicketAttachment(ticketId: string, attachmentId: string): Promise<void> {
    await api.delete(
      `/admin/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`
    );
  },

  async getAdminAttachmentBlob(ticketId: string, attachmentId: string): Promise<Blob> {
    const response = await api.get(
      `/admin/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { responseType: "blob" }
    );
    return response.data as Blob;
  },

  async openAdminAttachment(ticketId: string, attachmentId: string): Promise<void> {
    const blob = await ticketService.getAdminAttachmentBlob(ticketId, attachmentId);
    openBlobInNewTab(blob);
  },

  async getStorageSummary(): Promise<TicketStorageSummary> {
    const response = await api.get<TicketStorageSummary>("/admin/tickets/storage-summary");
    return response.data;
  },
};
